import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

interface SubscriptionCompleteResult {
  success: boolean;
  subscriptionId?: string;
  cardToken?: string;
  maskedCardNumber?: string;
  message?: string;
  error?: string;
  details?: any;
}

const SubscriptionComplete: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<SubscriptionCompleteResult | null>(null);

  useEffect(() => {
    const processEscottResponse = async () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptValue = searchParams.get('EncryptValue');

      if (!encryptValue) {
        setResult({
          success: false,
          error: 'eSCOTTからの応答データがありません',
        });
        setLoading(false);
        return;
      }

      try {
        // eSCOTTからの暗号化データをバックエンドで処理
        const response = await fetch('/api/webhooks/escott/process-result', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': localStorage.getItem('apiKey') || 'test-api-key',
          },
          body: JSON.stringify({
            encryptValue: encryptValue,
            source: 'subscription_complete',
          }),
        });

        const data = await response.json();

        if (data.success) {
          setResult({
            success: true,
            subscriptionId: data.data?.subscriptionId,
            cardToken: data.data?.cardToken,
            maskedCardNumber: data.data?.maskedCardNumber,
            message: 'サブスクリプションのカード登録が完了しました！',
            details: data.data,
          });
        } else {
          setResult({
            success: false,
            error: data.error || 'カード登録の処理に失敗しました',
            details: data,
          });
        }
      } catch (error) {
        console.error('eSCOTT結果処理エラー:', error);
        setResult({
          success: false,
          error: 'サーバーとの通信に失敗しました',
        });
      } finally {
        setLoading(false);
      }
    };

    processEscottResponse();
  }, [location.search]);

  const handleContinue = () => {
    navigate('/subscriptions', {
      state: {
        message: result?.success 
          ? 'サブスクリプションのカード登録が完了しました。' 
          : 'カード登録に問題が発生しました。',
        type: result?.success ? 'success' : 'error',
      },
    });
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          eSCOTTからの応答を処理中...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          カード登録結果を確認しています
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          {result?.success ? (
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          )}
          
          <Typography variant="h4" component="h1" textAlign="center">
            {result?.success ? 'カード登録完了' : 'カード登録エラー'}
          </Typography>
        </Box>

        {result?.success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {result.message}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {result?.error || '不明なエラーが発生しました'}
            </Typography>
          </Alert>
        )}

        {result?.success && (
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                登録情報
              </Typography>
              
              {result.subscriptionId && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>サブスクリプションID:</strong> {result.subscriptionId}
                </Typography>
              )}
              
              {result.maskedCardNumber && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>登録カード:</strong> {result.maskedCardNumber}
                </Typography>
              )}
              
              {result.cardToken && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>カードトークン:</strong> {result.cardToken.substring(0, 16)}...
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="flex-start">
            <InfoIcon sx={{ mr: 1, mt: 0.2 }} />
            <Box>
              <Typography variant="body2">
                <strong>セキュリティについて:</strong>
              </Typography>
              <Typography variant="body2">
                カード情報はeSCOTTによって安全に処理され、当システムには保存されていません。
                今後の決済は登録されたカードトークンを使用して実行されます。
              </Typography>
            </Box>
          </Box>
        </Alert>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{ minWidth: 200 }}
          >
            サブスクリプション一覧に戻る
          </Button>
        </Box>

        {/* Debug information (development only) */}
        {process.env.NODE_ENV === 'development' && result?.details && (
          <Card sx={{ mt: 3, bgcolor: 'grey.100' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                デバッグ情報
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                {JSON.stringify(result.details, null, 2)}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Container>
  );
};

export default SubscriptionComplete;